import React from 'react';
import { EnhancedDeliverySchedule } from '../../hooks/useSubscriptionData';
import { SubscriptionState } from '../../types';
import { DeliveryStatus } from '../../types/api';
import { useTranslation } from 'react-i18next';
import { formatDateLocale } from '../../utils/dateUtilsLocale';
interface DeliveryHistoryProps {
  deliveries: EnhancedDeliverySchedule[];
  onSkipDelivery: (deliveryId: string, date: string) => void;
  subscription: SubscriptionState;
  processingDeliveryId: string | null;
  loading: boolean;
  isPastView?: boolean;
}

const DeliveryHistory: React.FC<DeliveryHistoryProps> = ({
  deliveries,
  onSkipDelivery,
  subscription,
  processingDeliveryId,
  loading,
  isPastView = false
}) => {
  const { t } = useTranslation();

  if (deliveries.length === 0) {
    return (
      <div className="text-center py-8 text-gray-500">
        <p>{isPastView ? t('subscription.subscriptionDetails.empty.past') : t('subscription.subscriptionDetails.empty.upcoming')}</p>
      </div>
    );
  }

  // Helper function to check if a delivery is being processed
  const isDeliveryBeingProcessed = (delivery: EnhancedDeliverySchedule) => {
    if (!processingDeliveryId) return false;
    
    // Check if the delivery id matches the processing id directly
    if (delivery.id && delivery.id === processingDeliveryId) {
      return true;
    }
    
    // For calculated deliveries without id, check if the processing id contains the date
    const tempId = `temp-${subscription.id}-${delivery.date}`;
    return processingDeliveryId === tempId;
  };

  // Helper function to check if a delivery should have a skip button
  const shouldShowSkipButton = (delivery: EnhancedDeliverySchedule) => {
    // Don't show skip button for:
    // 1. Already skipped deliveries
    // 2. Delivered deliveries
    // 3. When subscription is not active
    return !isPastView && 
           delivery.status !== 'skipped' && 
           delivery.status !== 'delivered' && 
           subscription.status === 'active';
  };

  return (
    <div className="space-y-0">
      {deliveries.map((delivery, index) => (
        <div 
          key={`${delivery.id || delivery.date}-${index}`}
          className="border-b rounded-lg py-3 bg-white flex justify-between items-center"
        >
          <div className="flex flex-col gap-1.5">
            <div className="flex h-fit">
              <span className={`px-2 py-1 text-xs font-medium rounded-md ${
                delivery.status === 'delivered' ? 'bg-green-100 text-green-800' :
                delivery.status === 'skipped' ? 'bg-yellow-100 text-yellow-800' :
                'bg-blue-50 text-blue-800'
              }`}>
                {delivery.status === 'delivered' ? t('subscription.subscriptionDetails.status.delivered') :
                 delivery.status === 'skipped' ? t('subscription.subscriptionDetails.status.skipped') :
                 t('subscription.subscriptionDetails.status.scheduled')}
              </span>
              <h4 className="font-medium ml-2">
                {formatDateLocale(new Date(delivery.date), 'EEEE, MMMM d, yyyy')}
              </h4>
            </div>

              {/* Only show timeSlot for real database records, not for calculated/projected deliveries */}
              {(!(delivery as any).calculated && delivery.timeSlot) && (
                <p className="text-sm text-gray-400">
                  {delivery.timeSlot}
                </p>
              )}

          </div>
          
          {/* Only show skip button for upcoming deliveries that aren't already delivered or skipped */}
          {shouldShowSkipButton(delivery) && (
            <div className="flex justify-end">
              <button
                onClick={() => {
                  // We can skip any delivery as long as it has a date
                  // Pass the ID if available, otherwise just pass empty string
                  onSkipDelivery(delivery.id || '', delivery.date);
                }}
                disabled={loading || isDeliveryBeingProcessed(delivery)}
                className={`text-sm px-3 py-1 rounded-md ${
                  loading || isDeliveryBeingProcessed(delivery)
                    ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                    : 'bg-primary-50 text-primary-800 hover:bg-primary-100'
                }`}
              >
                {isDeliveryBeingProcessed(delivery)
                    ? t('subscription.subscriptionDetails.processingDelivery')
                    : t('subscription.subscriptionDetails.buttons.skip')}
                </button>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default DeliveryHistory; 